import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLeadSubmission } from "@/hooks/useLeadSubmission";
import { ArrowRight } from "lucide-react";
import techStackBg from "@/assets/tech-stack-bg.png";

interface MousePosition {
  x: number;
  y: number;
}

const BudgetRequestSection = () => {
  const { t } = useLanguage();
  const { submitLead, isSubmitting } = useLeadSubmission({ formType: "smm-audit" });
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const relativeScroll = window.scrollY - sectionTop + window.innerHeight;
        setScrollY(relativeScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
      const y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
      setMousePosition({ x: x * 0.5, y: y * 0.5 });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim()) {
      return;
    }

    const success = await submitLead(formData);
    
    if (success) {
      setFormData({
        name: "",
        phone: "",
        email: "",
      });
    }
  };

  const parallaxOffset = scrollY * 0.1;

  return (
    <section 
      ref={sectionRef}
      className="relative py-12 md:py-16 overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background with parallax and 3D effect */}
      <div 
        ref={containerRef}
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{
          transform: `
            perspective(1000px)
            translateY(${parallaxOffset}px)
            rotateX(${-mousePosition.y * 3}deg)
            rotateY(${mousePosition.x * 3}deg)
            scale(1.1)
          `,
          transformStyle: "preserve-3d",
        }}
      >
        <img
          src={techStackBg}
          alt="Technology Stack"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/85 to-primary/90" />
      </div>

      <div className="container relative z-10">
        <div className="max-w-xl mx-auto text-center">
          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-6">
            {t("budget.title")}
          </h2>

          {/* Form Card */}
          <div 
            className="relative"
            style={{
              transform: `
                perspective(1000px)
                translateX(${mousePosition.x * 10}px) 
                translateY(${mousePosition.y * 10}px)
                rotateX(${-mousePosition.y * 2}deg)
                rotateY(${mousePosition.x * 2}deg)
              `,
              transition: "transform 0.3s ease-out",
              transformStyle: "preserve-3d",
            }}
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-xl blur-lg opacity-50" />
            
            <div className="relative bg-background text-foreground rounded-xl p-5 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-xs">{t("budget.form.name")} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t("budget.form.namePlaceholder")}
                      maxLength={100}
                      required
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-xs">{t("budget.form.phone")} *</Label>
                    <PhoneInput
                      id="phone"
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                      required
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs">{t("budget.form.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@company.com"
                      maxLength={255}
                      className="h-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    t("budget.form.sending")
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      {t("budget.form.calculate")}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Promo Banner */}
          <div className="flex items-center justify-center gap-2 mt-5 p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
            <p className="text-sm font-semibold text-white">
              {t("budget.promo.banner")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BudgetRequestSection;
